<template>
  <div
    ref="action_plan_preview_container"
    :class="[
      'action-plan-preview-container',
      { 'preview-mode': preview },
      { 'loading-action-plan': !canContinue },
    ]"
    v-if="actionPlan"
  >
    <template v-for="(action, index) in actions">
      <!-- ======= Decision ======= -->
      <decision
        v-if="action.type === 'Decision'"
        :class="[{ 'mb-1 border-bottom': actions.length > 1 }]"
        :key="index"
        :action.sync="action"
        :action-plan-log-uuid="config.uuid || ''"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @answer-selected="onAnswerSelected"
        @checkpoint="onSaveCheckpoint"
      />

      <!-- ======= Decision Simple ======= -->
      <decision-simple
        v-if="action.type === 'DecisionSimple'"
        class="mb-2 border-bottom"
        :key="index"
        :action.sync="action"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
      />

      <!-- ======= Command ======= -->
      <command
        v-else-if="action.type === 'Node'"
        class="mb-1 border-bottom"
        :key="index"
        :action.sync="action"
        :action-plan-log-uuid="config.uuid || ''"
        :alarm-id="config.alarmId || null"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
        @checkpoint="onSaveCheckpoint"
      />

      <!-- ======= RestoreAlarmType ======= -->
      <restore-alarm-type
        v-else-if="action.type === 'RestoreAlarmType'"
        class="mb-1 border-bottom"
        :key="index"
        :action.sync="action"
        :action-plan-log-uuid="config.uuid || ''"
        :alarm-id="config.alarmId || null"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
        @checkpoint="onSaveCheckpoint"
      />

      <!-- ======= ShowVideo ======= -->
      <show-video
        v-else-if="action.type === 'ShowVideo'"
        class="mb-1 border-bottom"
        :key="index"
        :action.sync="action"
        :action-plan-log-uuid="config.uuid || 'aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee'"
        :alarm-id="config.alarmId || null"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
        @checkpoint="onSaveCheckpoint"
      />

      <!-- ======= Panel device status check ======= -->
      <panel-device-status-check
        v-else-if="action.type === 'PanelDeviceStatusCheck'"
        class="mb-1 border-bottom"
        :key="index"
        :action.sync="action"
        :action-plan-log-uuid="config.uuid"
        :alarm-id="config.alarmId || null"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
        @checkpoint="onSaveCheckpoint"
      />

      <!-- ======= Alarm comment ======= -->
      <alarm-comment
        v-else-if="action.type === 'AlarmComment'"
        class="mb-1 border-bottom"
        :key="index"
        :action.sync="action"
        :action-plan-log-uuid="config.uuid"
        :alarm-id="config.alarmId || null"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
        @checkpoint="onSaveCheckpoint"
      />

      <alarm-category
        v-else-if="action.type === 'AlarmCategory'"
        class="mb-1 border-bottom"
        :key="index"
        :action.sync="action"
        :alarm-id="config.alarmId || null"
        :alarm-type-id="config.alarmTypeId || null"
        :terminated="isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid"
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
        @checkpoint="onSaveCheckpoint"
      />

      <!-- ======= Validate device events ======= -->
      <validate-device-events
        v-else-if="action.type === 'ValidateDeviceEvents'"
        class="mb-1 border-bottom"
        :key="index"
        :action.sync="action"
        :action-plan-log-uuid="config.uuid"
        :alarm-id="config.alarmId || null"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
        @checkpoint="onSaveCheckpoint"
      />

      <!-- ======= Question ======= -->
      <question
        v-else-if="action.type === 'Question'"
        :key="index"
        :action.sync="action"
        :terminated="
          isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid
        "
        :preview="preview"
        :validating="validating"
        @continue="onContinueClick"
        @checkpoint="onSaveCheckpoint"
      />

      <!-- ======= Terminate ======= -->
      <div
        class="mb-1 pb-2 border-bottom"
        v-else-if="action.type === 'Terminate'"
        :key="index"
      >
        <p class="text-muted small">
          {{ $t("action_plan.viewer.finalized") }}
        </p>
      </div>

      <!-- ======= ActionPlan ======= -->
      <div
        class="mb-1 pb-2 border-bottom"
        v-else-if="action.type === 'ActionPlan'"
        :key="index"
      >
        <p class="h6 mb-2 fw-bold">
          {{ action.name }}
        </p>
      </div>

      <node-extras
        v-if="isInteractiveAction(action)"
        :key="`extras-${index}`"
        :action-plan-log-uuid="config.uuid || ''"
        :action="action"
        :viewer-options="viewerOptions"
        :terminated="isTerminated || action.data.action_plan_uuid !== currentActionPlanUuid"
        :preview="preview"
        :validating="validating"
        @checkpoint="onSaveCheckpoint"
      />
    </template>
  </div>
</template>

<script>
import Decision from "./Decision.vue";
import DecisionSimple from "./DecisionSimple.vue";
import Command from "./Command.vue";
import Question from "./Question.vue";
import ShowVideo from "./ShowVideo.vue";
import axios from "@axios";
import moment from "moment-timezone";
import {EventBus} from "@/libs/event-bus";
import {v4 as uuidv4} from "uuid";
import PanelDeviceStatusCheck from "./PanelDeviceStatusCheck.vue";
import AlarmComment from "./AlarmComment.vue";
import AlarmCategory from "./AlarmCategory.vue";
import RestoreAlarmType from "./RestoreAlarmType.vue";
import ValidateDeviceEvents from "./ValidateDeviceEvents.vue";
import NodeExtras from "./NodeExtras.vue";

export default {
  components: {
    AlarmComment,
    AlarmCategory,
    PanelDeviceStatusCheck,
    ShowVideo,
    Decision,
    DecisionSimple,
    Command,
    Question,
    RestoreAlarmType,
    ValidateDeviceEvents,
    NodeExtras,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    config: Object,
    actionPlan: {
      type: Object,
      default: [],
    },
    preview: Boolean,
    validating: Boolean,
    parentContainer: {
      type: HTMLDivElement,
      default: null,
    },
  },
  data() {
    return {
      currentActionPlanId: null,
      currentActionPlanUuid: null,
      actionPlanLogId: null,
      nodes: [],
      actions: [],
      container: null,
      terminated: false,
      actionPlanLocal: null,
      canContinue: true,
    };
  },
  created() {
    this.currentActionPlanId = this.actionPlan.id;
    this.actions = this.value.history || [];
    this.terminated = this.value.terminated;
    this.actionPlanLocal = this.actionPlan;
  },
  mounted() {
    this.container = this.parentContainer || this.$el;
    this.nodes = Object.keys(this.actionPlanLocal.nodes);

    if (!this.actions.length) {
      const root = this.actionPlanLocal.nodes[this.nodes[0]];
      root.actionPlanId = this.currentActionPlanId;

      const newActionPlan = {
        id: this.actionPlanId,
        type: "ActionPlan",
        name: this.actionPlanLocal.name,
        data: {
          timestamp: "",
          action_plan_id: "",
        },
      };

      this.addAction(newActionPlan, false);
      this.addAction(root);
    } else {
      const lastAction = this.getLatestAction();
      this.currentActionPlanUuid = lastAction.data.action_plan_uuid;
    }

    EventBus.on(`add_action_plan_${this.currentActionPlanId}`, this.onAddActionPlan);
  },
  beforeDestroy() {
    EventBus.off(`add_action_plan_${this.currentActionPlanId}`, this.onAddActionPlan);
  },
  computed: {
    isTerminated() {
      if (!this.actions.length && !this.actions.at(-1)) {
        return false;
      }

      return this.actions.at(-1)["type"] === "Terminate";
    },
    viewerOptions() {
      return this.actionPlanLocal?.viewer_options || {};
    },
  },
  watch: {
    actions(val) {
      this.$emit("input", {
        id: this.currentActionPlanId,
        terminated: this.isTerminated,
        history: val,
        config: this.config,
      });
    },
  },
  methods: {
    isInteractiveAction(action) {
      return [
        "Decision",
        "DecisionSimple",
        "Node",
        "RestoreAlarmType",
        "ShowVideo",
        "PanelDeviceStatusCheck",
        "AlarmComment",
        "AlarmCategory",
        "ValidateDeviceEvents",
        "Question",
      ].includes(action.type);
    },
    async addAction(action, logAction = true) {
      if (["Decision", "Node", "Terminate", "Question", "DecisionSimple"].includes(action.type)) {
        const ts = moment.tz("America/Mexico_City").format("YYYY-MM-DD HH:mm:ss");
        this.$set(action.data, "timestamp", ts);
      }
      this.$set(action.data, "action_plan_id", this.currentActionPlanId);
      this.$set(action.data, "action_plan_uuid", this.currentActionPlanUuid);

      this.terminated = action.type === "Terminate";
      this.actions.push(action);
      if (logAction) await this.updateLog();
      this.scrollToElement();
    },
    getFirstAction() {
      return this.actions.at(0);
    },
    getLatestAction() {
      return this.actions.at(-1);
    },
    async onAnswerSelected(data) {
      const {answer, action} = data;
      this.$set(action.data, "answer", answer);
      const nextId = action.node_data.outputs.find((o) => o.text === answer).id;
      await this.addAction(this.actionPlanLocal.nodes[nextId]);
    },
    async onContinueClick(action) {
      this.$set(action.data, "continue", true);
      const nextId = action.node_data.outputs.length
        ? action.node_data.outputs[0].id
        : null;
      if (nextId) await this.addAction(this.actionPlanLocal.nodes[nextId]);
    },
    async onSaveCheckpoint(onSuccess) {
      await this.updateLog();
      if (typeof onSuccess === "function") onSuccess();
    },
    loadActionPlan(actionPlanId) {
      return axios
        .get(`/v1/action_plan/get-action-plan-json/${actionPlanId}`)
        .then(({data}) => {
          const actionPlan = data.data;

          this.actionPlanLocal = actionPlan;
          this.currentActionPlanId = this.actionPlanLocal.id;
          this.currentActionPlanUuid = uuidv4();
          this.nodes = Object.keys(this.actionPlanLocal.nodes);

          const root = this.actionPlanLocal.nodes[this.nodes[0]];
          const newActionPlan = {
            id: this.currentActionPlanId,
            type: "ActionPlan",
            name: actionPlan.name,
            data: {
              action_plan_id: null,
              action_plan_uuid: null,
            },
          };

          this.addAction(newActionPlan);
          setTimeout(() => this.addAction(root), 500);
        });
    },
    onAddActionPlan(data) {
      this.loadActionPlan(data.actionPlanId);
    },
    async updateLog() {
      if (this.preview) return;

      this.canContinue = false;

      if (Object.keys(this.value).length > 0) {
        await axios.post("/v1/action_plan/update-action-plan-log", {
          id_action_plan: this.currentActionPlanId,
          uuid: this.config.uuid,
          id_alarm: this.config.alarmId,
          id_user: this.config.userId,
          log: this.value,
        });
      }

      this.$emit('update-action-plan-log');
      this.canContinue = true;
    },
    scrollToElement() {
      this.$nextTick(() => {
        this.container.scrollTop = this.container.scrollHeight;
      });
    },
  },
};
</script>

<style lang="scss">
.action-plan-preview-container {
  &.preview-mode {
    padding: 5px;
    max-height: 400px;
    overflow: auto;
    scroll-behavior: smooth;
    font-size: 0.875rem;
  }

  &.loading-action-plan {
    &:after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgb(209 209 209 / 43%);
    }
  }
}
</style>
